import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface TimeType {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

function calculateTimeLeft(targetTimestamp: number) {
  const now = new Date().getTime();
  const difference = targetTimestamp - now;

  let timeLeft: TimeType = {
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  };

  if (difference > 0) {
    timeLeft = {
      days: `${Math.floor(difference / (1000 * 60 * 60 * 24))}`.padStart(
        2,
        "0"
      ),
      hours: `${Math.floor((difference / (1000 * 60 * 60)) % 24)}`.padStart(
        2,
        "0"
      ),
      minutes: `${Math.floor((difference / 1000 / 60) % 60)}`.padStart(2, "0"),
      seconds: `${Math.floor((difference / 1000) % 60)}`.padStart(2, "0"),
    };
  }

  return timeLeft;
}

function useCountdown(targetTimestamp: number, toggle: () => void) {
  const [timeLeft, setTimeLeft] = useState<TimeType>(
    calculateTimeLeft(targetTimestamp)
  );

  useEffect(() => {
    const timer = setInterval(() => {
      if (
        timeLeft.days === "00" &&
        timeLeft.hours === "00" &&
        timeLeft.minutes === "00" &&
        timeLeft.seconds === "00"
      ) {
        toggle();
        clearInterval(timer);
      }

      setTimeLeft(calculateTimeLeft(targetTimestamp));
    }, 1000);

    return () => clearInterval(timer);
  }, [
    targetTimestamp,
    timeLeft.days,
    timeLeft.hours,
    timeLeft.minutes,
    timeLeft.seconds,
    toggle,
  ]);

  return timeLeft;
}

export function CountDown({
  timestamp,
  toggle,
}: {
  timestamp: number;
  toggle: () => void;
}) {
  const time = useCountdown(timestamp, toggle);
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-center text-[2.5rem] leading-[3rem] opacity-50 tracking-[2px] text-white">
        <p className="w-[54px]">{time.hours}</p>
        <p>:</p>
        <p className="w-[54px]">{time.minutes}</p>
        <p>:</p>
        <p className="w-[54px]">{time.seconds}</p>
      </div>

      <p className="text-sm font-medium text-white opacity-40 text-center">
        {t("next_time")}
      </p>
    </div>
  );
}
